import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Bell, MessageCircle, Zap, FileText, User as UserIcon } from "lucide-react";

const iconFor = (kind) => {
  if (["reminder", "digest", "escalation", "test"].includes(kind)) return MessageCircle;
  if (kind && kind.startsWith("eod_")) return FileText;
  if (kind && kind.startsWith("user_")) return UserIcon;
  return Zap;
};

const toneFor = (channel) => {
  if (channel === "whatsapp") return "bg-emerald-500/10 text-emerald-300 border-emerald-500/30";
  if (channel === "email") return "bg-indigo-500/10 text-indigo-300 border-indigo-500/30";
  return "bg-zinc-800 text-zinc-300 border-zinc-700";
};

export default function IngestionLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/alerts", { params: { limit: 200 } }).then((r) => setRows(r.data)); }, []);

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Audit Trail</div>
        <h1 className="font-display text-3xl font-bold">Audit Log</h1>
        <div className="text-sm text-zinc-400 mt-1">
          EOD submissions, edits, reminders, digests, and administrative actions.
        </div>
      </div>

      <Card className="bg-[#121215] border-[#27272a] divide-y divide-[#1f1f23]">
        {rows.length === 0 && (
          <div className="p-8 text-center text-zinc-500">
            <Bell className="w-6 h-6 mx-auto mb-2 opacity-50" />
            No events yet. Send a reminder from the dashboard or a test message from Settings.
          </div>
        )}
        {rows.map((r) => {
          const Icon = iconFor(r.kind);
          return (
            <div key={r.id} data-testid={`alert-row-${r.id}`} className="p-4 flex items-center gap-4">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${toneFor(r.channel)}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="border-[#3f3f46] text-zinc-400 text-[10px] uppercase">{r.kind}</Badge>
                  <span className="text-sm text-zinc-100 truncate">{r.target}</span>
                </div>
                <div className="text-xs text-zinc-500 truncate mt-0.5">{r.message}</div>
                {r.error && <div className="text-xs text-rose-300/90 truncate mt-0.5">⚠ {r.error}</div>}
              </div>
              <div className="text-right">
                <div className="text-xs text-zinc-500 font-mono">
                  {format(new Date(r.created_at), "MMM d · HH:mm")}
                </div>
                <Badge variant="outline" className={`text-[10px] mt-1 ${
                  r.status === "failed" ? "border-rose-500/40 text-rose-300"
                    : r.status === "sent" ? "border-emerald-500/40 text-emerald-300"
                    : "border-[#3f3f46] text-zinc-400"}`}>
                  {r.delivery_status && r.status !== "failed" ? r.delivery_status : r.status}
                </Badge>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}
